import { useDispatch, useSelector } from 'react-redux';

import { isolatedHeader } from 'Addons/fitmentSearch/isolatedKeys.ts';
import FacetToggle from 'Components/smallComponents/facetToggle.tsx';
import { createSelectRepeater, SelectContextProvider } from 'Components/smallComponents/select.tsx';
import {
  isolatedDiscardVehicle,
  replaceVehicleValue,
  discardVehicleFieldsRequested,
  vehicleSelected,
} from 'Core/actions/fitmentSearch/index.js';
import {
  createFilteredFitmentSearchResponseFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  createRequiredFitmentSearchFieldsSelector,
  createAllRequiredFieldsHaveSelectionSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchFieldsSelector,
  isSpecifyDialogOpenedSelector,
  fitmentSearchExtraFieldsSelector,
} from 'Core/selectors/fitmentSearch/index.js';
import { FacetValueFull, Vehicle } from 'Models/index.ts';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import { cloneSafe } from 'Utils/components.ts';

import type { FC } from 'react';
import type { TemplateFunction, TemplateResult } from 'Components/types.ts';
import type {
  Props as FacetToggleProps,
  Params as FacetToggleParams,
} from 'Components/smallComponents/facetToggle.tsx';
import type { VehicleValue } from 'Models/vehicle.ts';
import type { InnerProps as Props } from './vehicleDropdowns.tsx';

const ActualDropdowns: FC<Props> = ({
  template,
  name: widgetName,
  aboutToChange,
  appendedClasses,
  collapsing,
  facets,
  fields,
  isAutoVehicleSelectionDisabled: isAutoVehicleSelectionDisabledProp,
  isolatedKey,
  isVehicleSelected,
  facetToggleConfig,
  onSelectVehicle,
  onChangeVehicle,
  openDialog,
  rootRef,
  useNativeDropdown,
  globalVehicleDiscardEnabled,
  redirectUrl,
  doNotRedirectOnVehicleSelect,
  isVehicleSelectionForced,
  useSearchableDropdown,
  autoOpenDropdowns,
  isVehicleSelectionIsolated,
}) => {
  const dispatch = useDispatch();

  const responseFields = useSelector(
    createFilteredFitmentSearchResponseFieldsSelector(isolatedKey),
  ) as string[];
  const requiredFields = useSelector(createRequiredFitmentSearchFieldsSelector(isolatedKey)) as
    | string[]
    | null;
  const selection = useSelector(
    createFitmentSearchResponseSelectionSelector(isolatedKey),
  ) as FacetValueFull[];
  const isAllRequiredFieldHasSelection = useSelector(
    createAllRequiredFieldsHaveSelectionSelector(isolatedKey),
  ) as boolean;
  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);
  const isSpecifyDialogOpened = useSelector(isSpecifyDialogOpenedSelector);

  // if a user select a vehicle without some extra fields manually by the button,
  // mark those fields as ignored not to show the specify dialog for them
  const shouldNotIgnoreFields = fitmentSearchConfig.forceSpecifyDialog && isolatedKey === isolatedHeader;
  const vehicle = new Vehicle(
    responseFields
      .map(
        (field) =>
          selection.find((v) => v.field === field) ||
          (!requiredFields?.includes?.(field) &&
            !shouldNotIgnoreFields && { field, term: '__ignored', value: '__ignored' }),
      )
      .filter(Boolean) as VehicleValue[],
    responseFields,
  );

  const selectVehicle = () => {
    if (isAllRequiredFieldHasSelection) {
      dispatch(
        vehicleSelected(vehicle, {
          doNotRedirectOnVehicleSelect,
          isSpecifyDialogOpened,
          isVehicleSelectionForced,
          isolatedKey,
          redirectUrl,
          sendIsolatedRequest: isolatedKey !== null,
          vehicleWidgetName: widgetName,
        }),
      );

      fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
      onSelectVehicle();

      if (!collapsing.collapsed) {
        collapsing.toggleCollapsed();
      }
    }
  };
  const discardVehicle = () => {
    dispatch(isolatedDiscardVehicle(isolatedKey, globalVehicleDiscardEnabled));
    if (isAllRequiredFieldHasSelection) {
      fitmentSearchConfig.onVehicleDiscarded();
    }
  };
  const discardExtraFields = () =>
    dispatch(discardVehicleFieldsRequested(isolatedKey, fitmentFields, extraFitmentFields));
  const discardFields = (fields: string[]) =>
    dispatch(discardVehicleFieldsRequested(isolatedKey, fitmentFields, fields));

  const isVehicleSpecs = widgetName === 'VehicleSpecs';

  const getInitialOpenIndex = () => {
    const firstUnselectedIndex = facets.findIndex(
      ({ facetedValues }) => !facetedValues.some((v) => v.isSelected),
    );

    // If all fields have selections, don't auto-open any
    if (firstUnselectedIndex === -1) {
      return -1;
    }

    // If this is the first field, always open it
    if (firstUnselectedIndex === 0) {
      return 0;
    }

    // Otherwise, only open if all previous fields have selections
    const allPreviousSelected = facets
      .slice(0, firstUnselectedIndex)
      .every(({ facetedValues }) => facetedValues.some((v) => v.isSelected));

    return allPreviousSelected ? firstUnselectedIndex : -1;
  };

  const initialOpenIndex = getInitialOpenIndex();

  const selects = createSelectRepeater(
    facets.map(({ field, name, facetedValues }, index) => ({
      entries: facetedValues.map((v) => ({
        value: v.value,
        term: v.term,
        payload: v.payload,
        selected: v.isSelected,
      })),
      extraClassName: 'cm_vehicle-widget_select',
      disabled: !facetedValues.length,
      hideNullOption: true,
      field,
      title: name,
      key: field,
      index,
      useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
      isSearchable: useSearchableDropdown,
      initialOpen: autoOpenDropdowns && index === initialOpenIndex && facetedValues.length > 0,
      onChange: (term) => {
        const facetValue = facetedValues.find((v) => v.term === term);
        if (facetValue) {
          const isAutoVehicleSelectionDisabled =
            !isVehicleSpecs &&
            (isAutoVehicleSelectionDisabledProp ?? fitmentSearchConfig.isAutoVehicleSelectionDisabled);

          dispatch(
            replaceVehicleValue(facetValue, isolatedKey, fitmentFields, {
              isAutoVehicleSelectionDisabled,
              isPartialMode: !!fields,
              sendIsolatedRequest: isolatedKey !== null || isAutoVehicleSelectionDisabled,
              doNotRedirectOnVehicleSelect,
              redirectUrl,
              vehicleWidgetName: widgetName,
              isVehicleSelectionIsolated,
            }),
          );
        }
      },
    })),
  );

  const facetToggle = (templ: TemplateFunction<FacetToggleParams>) => {
    if (!facetToggleConfig) {
      return null;
    }

    const props = {
      ...facetToggleConfig,
      template: templ,
    } as FacetToggleProps;

    return (<FacetToggle {...props} key="facet-toggle" />) as TemplateResult;
  };

  const component = template.call({
    name: widgetName,
    selects,
    vehicle: vehicle.filteredFieldMap,
    vehicleString: vehicle.toString(),
    vehicleBaseValues: vehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: vehicle.getFilteredValues(extraFitmentFields),
    allowToSetVehicle: isAllRequiredFieldHasSelection,
    allowToDiscardVehicle: selection.length > 0,
    aboutToChange,
    discardVehicle,
    discardExtraFields,
    discardFields,
    selectVehicle,
    openDialog,
    facetToggle,
    changeVehicle: onChangeVehicle,
    fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
    template: 'active',
    isVehicleSelected,
    selection: Object.fromEntries(selection.map((select) => [select.field, select.value])),
    isVehicleSelectionForced,
    ...collapsing,
  });

  return <SelectContextProvider>{cloneSafe(component, rootRef, { appendedClasses })}</SelectContextProvider>;
};

export default ActualDropdowns;
